import { useEffect, useRef } from 'react';

/**
 * BuildVersionWatcher — detects new deploys and forces a reload.
 *
 * Problem: React SPAs cache the JS bundle forever (by hashed filename). If a
 * user keeps the tab open across a deploy, they run stale JS indefinitely.
 *
 * Solution: poll /api/ every 60s, compare `build` field to what we booted with.
 * If it changed (new deploy), show a toast and hard-reload once.
 */
const BuildVersionWatcher = () => {
  const bootBuildRef = useRef(null);
  const warnedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;

    const fetchBuild = async () => {
      try {
        const r = await fetch(`${backend}/api/`, { cache: 'no-store' });
        if (!r.ok) return null;
        const d = await r.json();
        return d?.build || null;
      } catch {
        return null;
      }
    };

    const boot = async () => {
      const b = await fetchBuild();
      if (b && !cancelled) bootBuildRef.current = b;
    };
    boot();

    const interval = setInterval(async () => {
      if (cancelled) return;
      const current = await fetchBuild();
      if (!current || !bootBuildRef.current) return;
      if (current !== bootBuildRef.current && !warnedRef.current) {
        warnedRef.current = true;
        console.log(
          `[BuildWatcher] New deploy detected: ${bootBuildRef.current} → ${current}. Reloading...`
        );
        // Give the UI 1s to finish any in-flight request, then hard-reload
        setTimeout(() => {
          try {
            // Wipe HTTP cache for this origin to pull the fresh index.html + new bundle
            if ('serviceWorker' in navigator) {
              navigator.serviceWorker
                .getRegistrations()
                .then((regs) => regs.forEach((r) => r.unregister()))
                .catch(() => {});
            }
          } catch {}
          // Use a cache-busting query on reload to force fresh fetch
          const url = new URL(window.location.href);
          url.searchParams.set('_b', current.slice(0, 10));
          window.location.replace(url.toString());
        }, 1000);
      }
    }, 60000); // every 60s

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return null;
};

export default BuildVersionWatcher;
